import { useState } from "react";
import { Button, CircularProgress, Tooltip } from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

// ─── helpers ───────────────────────────────────────────────────────────────────
const fmtINR  = (n)   => (n != null && n !== 0 ? `Rs.${Number(n).toLocaleString("en-IN")}` : "-");
const fmtDate = (d)   => (d ? new Date(d).toLocaleDateString("en-IN") : "-");
const fmtList = (arr) => (Array.isArray(arr) ? arr.join(", ") : arr || "-");
const safe    = (v)   => v || "-";
const fmtCurr = (n)   => (n != null ? n : 0);

const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

// ✅ GST is always calculated at this flat rate — never read from backend.
// (Same convention as the Excel export.)
const DEFAULT_BATTERY_GST = 5;
const DEFAULT_CHARGER_GST = 5;
const DEFAULT_BIKE_GST    = 5;

// ✅ GST-exclusive breakdown — amount is treated as GST-inclusive,
// base is derived by dividing out the rate first, then split into CGST+SGST.
const gstBreakdown = (amount, rate) => {
  const amt = fmtCurr(amount);
  const r = rate || 0;
  const taxable = r > 0 ? round2(amt / (1 + r / 100)) : amt;
  const cgst = round2((taxable * (r / 2)) / 100);
  const sgst = round2((taxable * (r / 2)) / 100);
  const total = round2(taxable + cgst + sgst);
  return { taxable, rate: r, cgst, sgst, total };
};

// ✅ per-bike component amounts + rates (rates always the flat default — no backend lookup)
const batteryTotalAmount = (b) =>
  Array.isArray(b.battery) ? b.battery.reduce((s, x) => s + fmtCurr(x.batteryAmount), 0) : 0;
const batteryGstRate = () => DEFAULT_BATTERY_GST;

const chargerAmount = (b) => (b.charger ? fmtCurr(b.charger.chargerAmount) : 0);
const chargerGstRate = () => DEFAULT_CHARGER_GST;

const bikeGstRate = () => DEFAULT_BIKE_GST;

// ✅ the 3 GST breakdowns per bike row
const batteryGst = (b) => gstBreakdown(batteryTotalAmount(b), batteryGstRate());
const chargerGst = (b) => gstBreakdown(chargerAmount(b), chargerGstRate());
// Bike-only amount = total sell price minus battery+charger amounts bundled into it
const bikeOnlyAmount = (b) =>
  Math.max(fmtCurr(b.salePrice) - batteryTotalAmount(b) - chargerAmount(b), 0);
const bikeGst = (b) => gstBreakdown(bikeOnlyAmount(b), bikeGstRate());

const totalGstPayable = (b) =>
  round2(
    bikeGst(b).cgst + bikeGst(b).sgst +
    batteryGst(b).cgst + batteryGst(b).sgst +
    chargerGst(b).cgst + chargerGst(b).sgst
  );

const fmtBatteryList = (batteries) => {
  if (!Array.isArray(batteries) || !batteries.length) return "-";
  return batteries
    .map((bt) => {
      const stock = bt.batteryStock || {};
      const label = safe(stock.model) !== "-" ? stock.model : "Battery";
      const sn = stock.serialNo ? ` (SN:${stock.serialNo})` : "";
      return `${label}${sn}`;
    })
    .join("; ");
};

const fmtCharger = (charger) => {
  if (!charger) return "-";
  const stock = charger.chargerStock || {};
  const label = safe(stock.model) !== "-" ? stock.model : "Charger";
  const sn = stock.serialNo ? ` (SN:${stock.serialNo})` : "";
  return `${label}${sn}`;
};

const buildLabel = (filters) => {
  if (filters.fromDate && filters.toDate)
    return `Date Range: ${filters.fromDate}  to  ${filters.toDate}`;
  if (filters.month && filters.year)
    return `Month: ${new Date(0, filters.month - 1).toLocaleString("default", { month: "long" })} ${filters.year}`;
  if (filters.year)     return `Year: ${filters.year}`;
  if (filters.month)    return `Month: ${new Date(0, filters.month - 1).toLocaleString("default", { month: "long" })}`;
  if (filters.location) return `Location: ${filters.location}`;
  if (filters.vehicleType) return `Type: ${filters.vehicleType}`;
  return "All Records";
};

// ─── Load jsPDF + autoTable from CDN ──────────────────────────────────────────
const loadScript = (src) =>
  new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) return resolve();
    const s = document.createElement("script");
    s.src = src;
    s.onload = resolve;
    s.onerror = reject;
    document.head.appendChild(s);
  });

const ensureJsPDF = async () => {
  await loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js");
  await loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.2/jspdf.plugin.autotable.min.js");
};

// ─── COLORS ───────────────────────────────────────────────────────────────────
const C = {
  primary:   [21, 101, 192],
  green:     [27, 94, 32],
  orange:    [230, 81, 0],
  purple:    [74, 20, 140],
  pink:      [136, 14, 79],
  teal:      [0, 105, 92],
  grey:      [85, 85, 85],
  white:     [255, 255, 255],
  header:    [13, 27, 42],
  rowAlt:    [250, 251, 255],
  due:       [255, 248, 245],
  lightBlue: [227, 242, 253],
};

// ─── VEHICLE TYPE COLOR ───────────────────────────────────────────────────────
const vtColor = (vt) => (vt === "E_RICKSHAW" ? C.green : C.purple);

// ─── PDF GENERATOR ────────────────────────────────────────────────────────────
const generatePDF = async (data, filters) => {
  await ensureJsPDF();
  const { jsPDF } = window.jspdf;
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });

  const PW  = doc.internal.pageSize.getWidth();
  const PH  = doc.internal.pageSize.getHeight();
  const label    = buildLabel(filters);
  const today    = new Date().toLocaleDateString("en-IN");
  const filename = `EV_Sales_${new Date().toISOString().split("T")[0]}.pdf`;

  // ── Reusable header ─────────────────────────────────────────────────────────
  const drawPageHeader = (title, accentColor = C.primary) => {
    doc.setFillColor(...C.header);
    doc.rect(0, 0, PW, 40, "F");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(15);
    doc.setTextColor(...C.white);
    doc.text("EV Sales Report", 30, 26);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(180, 200, 220);
    doc.text(label, 30, 38);

    doc.setFillColor(...accentColor);
    doc.rect(0, 42, PW, 22, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(...C.white);
    doc.text(title, 30, 57);

    doc.setFontSize(8);
    doc.setTextColor(200, 220, 255);
    doc.text(`Generated: ${today}  |  Total Records: ${data.length}`, PW - 30, 57, { align: "right" });

    return 72;
  };

  // ── Reusable footer ─────────────────────────────────────────────────────────
  const drawFooter = () => {
    const pages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pages; i++) {
      doc.setPage(i);
      doc.setFillColor(240, 244, 248);
      doc.rect(0, PH - 20, PW, 20, "F");
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.5);
      doc.setTextColor(...C.grey);
      doc.text("EV Showroom Management System  |  Confidential", 30, PH - 7);
      doc.text(`Page ${i} of ${pages}`, PW - 30, PH - 7, { align: "right" });
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // PAGE 1 — EXECUTIVE SUMMARY DASHBOARD
  // ═══════════════════════════════════════════════════════════════════════════
  let y = drawPageHeader("Executive Summary — All Vehicles");

  const eRick   = data.filter((b) => b.vehicleType === "E_RICKSHAW");
  const scooty  = data.filter((b) => b.vehicleType === "SCOOTY");
  const dueList = data.filter((b) => b.due?.status === "DUE");
  const exchDue = data.filter((b) => b.exchange?.status === "DUE");

  const totalRev      = data.reduce((s, b) => s + (b.salePrice || 0), 0);
  const totalORP      = data.reduce((s, b) => s + (b.orp || 0), 0);
  const totalDueRem   = data.reduce((s, b) => s + (b.due?.remainingAmount || 0), 0);
  const totalExchRem  = data.reduce((s, b) => s + (b.exchange?.remainingAmount || 0), 0);
  const totalFinance  = data.reduce((s, b) => s + (b.finance?.amount || 0), 0);
  const totalGstAll   = data.reduce((s, b) => s + totalGstPayable(b), 0);

  // KPI cards
  const kpis = [
    { label: "Total EV Sold",        value: data.length,            color: C.primary },
    { label: "E-Rickshaw Sold",      value: eRick.length,           color: C.green },
    { label: "Scooty Sold",          value: scooty.length,          color: C.purple },
    { label: "ExShowroom Revenue",   value: fmtINR(totalRev),       color: C.primary },
    { label: "ORP Revenue",          value: fmtINR(totalORP),       color: C.green },
    { label: "Total Finance Amt",    value: fmtINR(totalFinance),   color: C.purple },
    { label: "Due Pending Customers",value: `${dueList.length}`,    color: C.orange },
    { label: "Pending Due Amt",      value: fmtINR(totalDueRem),    color: C.orange },
    { label: "Exchange Pending",     value: fmtINR(totalExchRem),   color: C.pink },
    { label: "Total GST Payable (5%)", value: fmtINR(totalGstAll),  color: C.teal },
  ];

  const cardW   = (PW - 60) / 3;
  const cardH   = 50;
  const cardGap = 8;

  kpis.forEach((k, idx) => {
    const col = idx % 3;
    const row = Math.floor(idx / 3);
    const x   = 30 + col * (cardW + cardGap);
    const cy  = y + row * (cardH + 6);

    doc.setFillColor(...C.white);
    doc.setDrawColor(...k.color);
    doc.setLineWidth(1.2);
    doc.roundedRect(x, cy, cardW, cardH, 4, 4, "FD");

    doc.setFillColor(...k.color);
    doc.roundedRect(x, cy, 5, cardH, 2, 2, "F");

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...C.grey);
    doc.text(k.label, x + 13, cy + 15);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.setTextColor(...k.color);
    doc.text(String(k.value), x + 13, cy + 35);
  });

  y += 4 * (cardH + 6) + 16;

  // Location × VehicleType summary table
  const locations = [...new Set(data.map((b) => b.location || "Other"))];
  const sumHead = [[
    "Location", "Vehicle Type", "Total", "CASH", "FINANCE", "EXCHANGE", "EX+FIN", "CASH+EX",
    "PAID", "DUE", "ExShowroom (Rs.)", "ORP (Rs.)", "Finance (Rs.)", "Due Remaining (Rs.)", "Exch. Remaining (Rs.)"
  ]];
  const sumBody = [];
  locations.forEach((loc) => {
    ["ALL", "E_RICKSHAW", "SCOOTY"].forEach((vt) => {
      const bikes = data.filter((b) =>
        (b.location || "Other") === loc &&
        (vt === "ALL" || b.vehicleType === vt)
      );
      if (!bikes.length) return;
      sumBody.push([
        loc, vt === "ALL" ? "All" : vt,
        bikes.length,
        bikes.filter((b) => b.saleType === "CASH").length,
        bikes.filter((b) => b.saleType === "FINANCE").length,
        bikes.filter((b) => b.saleType === "EXCHANGE").length,
        bikes.filter((b) => b.saleType === "EX+FIN").length,
        bikes.filter((b) => b.saleType === "CASH+EX").length,
        bikes.filter((b) => b.due?.status !== "DUE").length,
        bikes.filter((b) => b.due?.status === "DUE").length,
        fmtINR(bikes.reduce((s, b) => s + (b.salePrice || 0), 0)),
        fmtINR(bikes.reduce((s, b) => s + (b.orp || 0), 0)),
        fmtINR(bikes.reduce((s, b) => s + (b.finance?.amount || 0), 0)),
        fmtINR(bikes.reduce((s, b) => s + (b.due?.remainingAmount || 0), 0)),
        fmtINR(bikes.reduce((s, b) => s + (b.exchange?.remainingAmount || 0), 0)),
      ]);
    });
  });
  // Grand total
  sumBody.push([
    "GRAND TOTAL", "ALL", data.length,
    data.filter((b) => b.saleType === "CASH").length,
    data.filter((b) => b.saleType === "FINANCE").length,
    data.filter((b) => b.saleType === "EXCHANGE").length,
    data.filter((b) => b.saleType === "EX+FIN").length,
    data.filter((b) => b.saleType === "CASH+EX").length,
    data.filter((b) => b.due?.status !== "DUE").length,
    dueList.length,
    fmtINR(totalRev), fmtINR(totalORP), fmtINR(totalFinance),
    fmtINR(totalDueRem), fmtINR(totalExchRem),
  ]);

  doc.autoTable({
    startY: y,
    head: sumHead,
    body: sumBody,
    styles:       { font: "helvetica", fontSize: 7, cellPadding: 3 },
    headStyles:   { fillColor: C.primary, textColor: C.white, fontStyle: "bold", halign: "center" },
    columnStyles: {
      0: { fontStyle: "bold", cellWidth: 65 },
      1: { cellWidth: 55 },
      10: { halign: "right" }, 11: { halign: "right" },
      12: { halign: "right" },
      13: { halign: "right", textColor: C.orange },
      14: { halign: "right", textColor: C.pink },
    },
    didParseCell: ({ row, cell }) => {
      if (row.index === sumBody.length - 1) {
        cell.styles.fillColor = C.lightBlue;
        cell.styles.fontStyle = "bold";
        cell.styles.textColor = C.primary;
      }
    },
    margin: { left: 20, right: 20 },
  });

  // ═══════════════════════════════════════════════════════════════════════════
  // PAGES — FULL RECORDS BY VEHICLE TYPE × LOCATION
  // ═══════════════════════════════════════════════════════════════════════════
  ["E_RICKSHAW", "SCOOTY"].forEach((vt) => {
    const vtData = data.filter((b) => b.vehicleType === vt);
    if (!vtData.length) return;

    locations.forEach((loc) => {
      const bikes = vtData.filter((b) => (b.location || "Other") === loc);
      if (!bikes.length) return;

      doc.addPage();
      const color  = vtColor(vt);
      const startY = drawPageHeader(`${vt === "E_RICKSHAW" ? "E-Rickshaw" : "Scooty"} — ${loc} (${bikes.length} records)`, color);

      const head = [[
        "#", "Invoice", "Date", "Company", "Model",
        "Engine No", "Serial No", "No. Plate",
        "Customer", "Mobile",
        "ExShowroom", "ORP", "Discount",
        "Sale Type", "Finance Co.", "Finance Amt",
        "Due Status", "Due Remaining"
      ]];

      const body = bikes.map((b, i) => [
        i + 1,
        safe(b.invoiceNo),
        fmtDate(b.saleDate),
        safe(b.companyName),
        safe(b.model),
        safe(b.engineNumber),
        safe(b.chasisNumber),
        safe(b.noPlate),
        safe(b.customerName),
        fmtList(b.mobile),
        fmtINR(b.salePrice),
        fmtINR(b.orp),
        fmtINR(b.discount),
        safe(b.saleType),
        safe(b.finance?.companyName),
        fmtINR(b.finance?.amount),
        b.due?.status || "PAID",
        b.due?.status === "DUE" ? fmtINR(b.due?.remainingAmount) : "-",
      ]);

      doc.autoTable({
        startY,
        head,
        body,
        styles:             { font: "helvetica", fontSize: 7, cellPadding: 3 },
        headStyles:         { fillColor: color, textColor: C.white, fontStyle: "bold", fontSize: 7.5 },
        alternateRowStyles: { fillColor: C.rowAlt },
        columnStyles: {
          0:  { cellWidth: 16, halign: "center" },
          1:  { cellWidth: 68, textColor: C.primary },
          2:  { cellWidth: 44 },
          3:  { cellWidth: 52 },
          4:  { cellWidth: 52, fontStyle: "bold" },
          5:  { cellWidth: 58 },
          6:  { cellWidth: 58 },
          7:  { cellWidth: 44 },
          8:  { cellWidth: 68 },
          9:  { cellWidth: 62 },
          10: { cellWidth: 52, halign: "right" },
          11: { cellWidth: 48, halign: "right" },
          12: { cellWidth: 44, halign: "right" },
          13: { cellWidth: 40, halign: "center" },
          14: { cellWidth: 52 },
          15: { cellWidth: 52, halign: "right" },
          16: { cellWidth: 32, halign: "center" },
          17: { cellWidth: 54, halign: "right" },
        },
        didParseCell: ({ row, cell, column }) => {
          const bike = bikes[row.index];
          if (!bike) return;
          if (bike.due?.status === "DUE") cell.styles.fillColor = C.due;
          if (column.index === 16) {
            cell.styles.textColor = bike.due?.status === "DUE" ? C.orange : C.green;
            cell.styles.fontStyle = "bold";
          }
          if (column.index === 17 && bike.due?.status === "DUE") {
            cell.styles.textColor = C.orange;
            cell.styles.fontStyle = "bold";
          }
        },
        margin: { left: 18, right: 18 },
      });
    });
  });

  // ═══════════════════════════════════════════════════════════════════════════
  // PAGE — COMPLETE DETAIL (all fields)
  // ═══════════════════════════════════════════════════════════════════════════
  doc.addPage();
  const detY = drawPageHeader("Complete Records — All Fields", C.header);

  const detHead = [[
    "#", "Invoice", "Date", "Location", "VehicleType",
    "Company", "Model", "Engine No", "Serial No", "No. Plate",
    "Customer", "Father/CO", "Mobile", "Village",
    "GSTIN", "Cash Amt", "Coupon",
    "RTO", "Insurance",
    "Helmet", "Seat Cover", "Transport", "TCR", "Reg Broker",
    "GST%", "Total GST",
    "Gifts", "Sold By", "Remarks"
  ]];

  const detBody = data.map((b, i) => [
    i + 1, safe(b.invoiceNo), fmtDate(b.saleDate), safe(b.location), safe(b.vehicleType),
    safe(b.companyName), safe(b.model), safe(b.engineNumber), safe(b.chasisNumber), safe(b.noPlate),
    safe(b.customerName), safe(b.fatherName), fmtList(b.mobile), safe(b.village),
    safe(b.GSTIN), fmtINR(b.cashAmount), fmtINR(b.couponCashback),
    fmtINR(b.charges?.rto), fmtINR(b.charges?.insurance),
    fmtINR(b.expenses?.helmet), fmtINR(b.expenses?.seatCover),
    fmtINR(b.expenses?.transport), fmtINR(b.expenses?.tcr), fmtINR(b.expenses?.regBroker),
    safe(b.tax?.gstRate), fmtINR(b.tax?.totalGstAmount),
    (b.gifts || []).map((g) => `${g.name}×${g.quantity}`).join("; ") || "-",
    safe(b.soldBy?.name), safe(b.remarks),
  ]);

  doc.autoTable({
    startY: detY,
    head: detHead,
    body: detBody,
    styles:             { font: "helvetica", fontSize: 6, cellPadding: 2 },
    headStyles:         { fillColor: C.header, textColor: C.white, fontStyle: "bold", fontSize: 6.5 },
    alternateRowStyles: { fillColor: C.rowAlt },
    margin:             { left: 14, right: 14 },
  });

  // ═══════════════════════════════════════════════════════════════════════════
  // PAGE — GST BREAKDOWN (per bike: Battery + Charger + Bike-only, always @5%)
  // Mirrors the Excel "All Records" GST columns.
  // ═══════════════════════════════════════════════════════════════════════════
  doc.addPage();
  const gstY = drawPageHeader(`GST Breakdown — Battery + Charger + Bike (flat ${DEFAULT_BIKE_GST}%)`, C.teal);

  const gstHead = [[
    "#", "Invoice", "Customer", "Model",
    "Battery Taxable", "Batt. CGST", "Batt. SGST", "Batt. Total",
    "Charger Taxable", "Chg. CGST", "Chg. SGST", "Chg. Total",
    "Bike Taxable", "Bike CGST", "Bike SGST", "Bike Total",
    "Total GST Payable"
  ]];

  const gstBody = data.map((b, i) => {
    const bg = batteryGst(b);
    const cg = chargerGst(b);
    const kg = bikeGst(b);
    return [
      i + 1,
      safe(b.invoiceNo),
      safe(b.customerName),
      safe(b.model),
      fmtINR(bg.taxable), fmtINR(bg.cgst), fmtINR(bg.sgst), fmtINR(bg.total),
      fmtINR(cg.taxable), fmtINR(cg.cgst), fmtINR(cg.sgst), fmtINR(cg.total),
      fmtINR(kg.taxable), fmtINR(kg.cgst), fmtINR(kg.sgst), fmtINR(kg.total),
      fmtINR(totalGstPayable(b)),
    ];
  });

  // Grand total row
  const gTaxableSum = (fn) => data.reduce((s, b) => s + fn(b).taxable, 0);
  const gCgstSum    = (fn) => data.reduce((s, b) => s + fn(b).cgst, 0);
  const gSgstSum    = (fn) => data.reduce((s, b) => s + fn(b).sgst, 0);
  const gTotalSum   = (fn) => data.reduce((s, b) => s + fn(b).total, 0);

  gstBody.push([
    "", "GRAND TOTAL", "", "",
    fmtINR(gTaxableSum(batteryGst)), fmtINR(gCgstSum(batteryGst)), fmtINR(gSgstSum(batteryGst)), fmtINR(gTotalSum(batteryGst)),
    fmtINR(gTaxableSum(chargerGst)), fmtINR(gCgstSum(chargerGst)), fmtINR(gSgstSum(chargerGst)), fmtINR(gTotalSum(chargerGst)),
    fmtINR(gTaxableSum(bikeGst)), fmtINR(gCgstSum(bikeGst)), fmtINR(gSgstSum(bikeGst)), fmtINR(gTotalSum(bikeGst)),
    fmtINR(totalGstAll),
  ]);

  doc.autoTable({
    startY: gstY,
    head: gstHead,
    body: gstBody,
    styles:             { font: "helvetica", fontSize: 6.5, cellPadding: 2.5 },
    headStyles:         { fillColor: C.teal, textColor: C.white, fontStyle: "bold", fontSize: 6.5, halign: "center" },
    alternateRowStyles: { fillColor: C.rowAlt },
    columnStyles: {
      0: { cellWidth: 16, halign: "center" },
      4: { halign: "right" }, 5: { halign: "right" }, 6: { halign: "right" }, 7: { halign: "right", fontStyle: "bold" },
      8: { halign: "right" }, 9: { halign: "right" }, 10: { halign: "right" }, 11: { halign: "right", fontStyle: "bold" },
      12: { halign: "right" }, 13: { halign: "right" }, 14: { halign: "right" }, 15: { halign: "right", fontStyle: "bold" },
      16: { halign: "right", fontStyle: "bold", textColor: C.teal },
    },
    didParseCell: ({ row, cell }) => {
      if (row.index === gstBody.length - 1) {
        cell.styles.fillColor = [224, 242, 241];
        cell.styles.fontStyle = "bold";
        cell.styles.textColor = C.teal;
      }
    },
    margin: { left: 14, right: 14 },
  });

  // ═══════════════════════════════════════════════════════════════════════════
  // PAGE — BATTERY & CHARGER SOLD (one row per unit, flat 5% GST)
  // Mirrors Excel Sheet 7 "Battery & Charger Sold".
  // ═══════════════════════════════════════════════════════════════════════════
  const bcRows = [];
  data.forEach((b) => {
    (b.battery || []).forEach((bt) => {
      const stock = bt.batteryStock || {};
      const g = gstBreakdown(bt.batteryAmount, DEFAULT_BATTERY_GST);
      bcRows.push({
        invoiceNo: b.invoiceNo, saleDate: b.saleDate, customerName: b.customerName,
        vehicleType: b.vehicleType, model: b.model,
        itemType: "BATTERY", companyName: stock.companyName, itemModel: stock.model,
        serialNo: stock.serialNo, amount: bt.batteryAmount, g,
      });
    });
    if (b.charger) {
      const stock = b.charger.chargerStock || {};
      const g = gstBreakdown(b.charger.chargerAmount, DEFAULT_CHARGER_GST);
      bcRows.push({
        invoiceNo: b.invoiceNo, saleDate: b.saleDate, customerName: b.customerName,
        vehicleType: b.vehicleType, model: b.model,
        itemType: "CHARGER", companyName: stock.companyName, itemModel: stock.model,
        serialNo: stock.serialNo, amount: b.charger.chargerAmount, g,
      });
    }
  });

  if (bcRows.length > 0) {
    doc.addPage();
    const bcY = drawPageHeader(`Battery & Charger Sold — ${bcRows.length} Units (flat ${DEFAULT_BATTERY_GST}% GST)`, C.teal);

    const bcHead = [[
      "Invoice", "Date", "Customer", "Bike Model", "Item Type",
      "Company", "Item Model", "Serial No",
      "Amount", "Taxable", "GST%", "CGST", "SGST", "Total Payable"
    ]];
    const bcBody = bcRows.map((r) => [
      safe(r.invoiceNo), fmtDate(r.saleDate), safe(r.customerName), safe(r.model), r.itemType,
      safe(r.companyName), safe(r.itemModel), safe(r.serialNo),
      fmtINR(r.amount), fmtINR(r.g.taxable), `${r.g.rate}%`, fmtINR(r.g.cgst), fmtINR(r.g.sgst), fmtINR(r.g.total),
    ]);

    const bcTotals = {
      amount: bcRows.reduce((s, r) => s + fmtCurr(r.amount), 0),
      taxable: bcRows.reduce((s, r) => s + r.g.taxable, 0),
      cgst: bcRows.reduce((s, r) => s + r.g.cgst, 0),
      sgst: bcRows.reduce((s, r) => s + r.g.sgst, 0),
      total: bcRows.reduce((s, r) => s + r.g.total, 0),
    };
    bcBody.push([
      "", "", "", "", "TOTAL", "", "", "",
      fmtINR(bcTotals.amount), fmtINR(bcTotals.taxable), "", fmtINR(bcTotals.cgst), fmtINR(bcTotals.sgst), fmtINR(bcTotals.total),
    ]);

    doc.autoTable({
      startY: bcY,
      head: bcHead,
      body: bcBody,
      styles:             { font: "helvetica", fontSize: 7.5, cellPadding: 3.5 },
      headStyles:         { fillColor: C.teal, textColor: C.white, fontStyle: "bold" },
      alternateRowStyles: { fillColor: [230, 245, 243] },
      columnStyles: {
        8: { halign: "right" }, 9: { halign: "right" }, 10: { halign: "center" },
        11: { halign: "right" }, 12: { halign: "right" },
        13: { halign: "right", fontStyle: "bold", textColor: C.teal },
      },
      didParseCell: ({ row, cell }) => {
        if (row.index === bcBody.length - 1) {
          cell.styles.fillColor = [224, 242, 241];
          cell.styles.fontStyle = "bold";
          cell.styles.textColor = C.teal;
        }
      },
      margin: { left: 20, right: 20 },
    });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PAGE — EXCHANGE DETAILS
  // ═══════════════════════════════════════════════════════════════════════════
  const exchData = data.filter((b) => b.exchange?.exchangePrice > 0);
  if (exchData.length > 0) {
    doc.addPage();
    drawPageHeader(`Exchange Vehicles — ${exchData.length} Records`, C.pink);

    const exHead = [[
      "#", "Invoice", "Date", "Location", "Customer",
      "Exch. Owner", "Phone", "Exch. Model", "Reg No",
      "Agreed Price (Rs.)", "Paid (Rs.)", "Remaining (Rs.)", "Status"
    ]];
    const exBody = exchData.map((b, i) => [
      i + 1, safe(b.invoiceNo), fmtDate(b.saleDate), safe(b.location), safe(b.customerName),
      safe(b.exchange?.ownerName), safe(b.exchange?.phone), safe(b.exchange?.model),
      safe(b.exchange?.registrationNumber),
      fmtINR(b.exchange?.exchangePrice), fmtINR(b.exchange?.paidAmount),
      fmtINR(b.exchange?.remainingAmount), safe(b.exchange?.status),
    ]);
    exBody.push([
      "", "TOTAL", "", "", "", "", "", "", "",
      fmtINR(exchData.reduce((s, b) => s + (b.exchange?.exchangePrice || 0), 0)),
      fmtINR(exchData.reduce((s, b) => s + (b.exchange?.paidAmount || 0), 0)),
      fmtINR(exchData.reduce((s, b) => s + (b.exchange?.remainingAmount || 0), 0)),
      "",
    ]);

    doc.autoTable({
      startY: 72,
      head: exHead,
      body: exBody,
      styles:       { font: "helvetica", fontSize: 7.5, cellPadding: 4 },
      headStyles:   { fillColor: C.pink, textColor: C.white, fontStyle: "bold" },
      alternateRowStyles: { fillColor: [255, 248, 252] },
      columnStyles: {
        9:  { halign: "right" },
        10: { halign: "right", textColor: C.green },
        11: { halign: "right", textColor: C.orange, fontStyle: "bold" },
      },
      didParseCell: ({ row, cell, column }) => {
        const bike = exchData[row.index];
        if (row.index === exBody.length - 1) {
          cell.styles.fillColor = [255, 243, 224];
          cell.styles.fontStyle = "bold";
          cell.styles.textColor = C.orange;
        }
        if (bike && column.index === 12) {
          cell.styles.textColor = bike.exchange?.status === "DUE" ? C.orange : C.green;
          cell.styles.fontStyle = "bold";
        }
      },
      margin: { left: 30, right: 30 },
    });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PAGE — DUE PAYMENTS
  // ═══════════════════════════════════════════════════════════════════════════
  if (dueList.length > 0) {
    doc.addPage();
    drawPageHeader(`Due Payments — ${dueList.length} Pending`, C.orange);

    const dueHead = [[
      "#", "Invoice", "Date", "Location", "Vehicle Type",
      "Company", "Model", "Customer", "Mobile",
      "ExShowroom (Rs.)", "Sale Type",
      "Total Due (Rs.)", "Paid (Rs.)", "Remaining (Rs.)", "Address"
    ]];
    const dueBody = dueList.map((b, i) => [
      i + 1, safe(b.invoiceNo), fmtDate(b.saleDate), safe(b.location), safe(b.vehicleType),
      safe(b.companyName), safe(b.model), safe(b.customerName), fmtList(b.mobile),
      fmtINR(b.salePrice), safe(b.saleType),
      fmtINR(b.due?.totalAmount), fmtINR(b.due?.paidAmount), fmtINR(b.due?.remainingAmount),
      safe(b.address),
    ]);
    dueBody.push([
      "", "TOTAL", "", "", "", "", "", "", "",
      fmtINR(dueList.reduce((s, b) => s + (b.salePrice || 0), 0)), "",
      fmtINR(dueList.reduce((s, b) => s + (b.due?.totalAmount || 0), 0)),
      fmtINR(dueList.reduce((s, b) => s + (b.due?.paidAmount || 0), 0)),
      fmtINR(dueList.reduce((s, b) => s + (b.due?.remainingAmount || 0), 0)),
      "",
    ]);

    doc.autoTable({
      startY: 72,
      head: dueHead,
      body: dueBody,
      styles:             { font: "helvetica", fontSize: 7.5, cellPadding: 4 },
      headStyles:         { fillColor: C.orange, textColor: C.white, fontStyle: "bold" },
      alternateRowStyles: { fillColor: C.due },
      columnStyles: {
        9:  { halign: "right" },
        11: { halign: "right" },
        12: { halign: "right", textColor: C.green },
        13: { halign: "right", textColor: C.orange, fontStyle: "bold" },
      },
      didParseCell: ({ row, cell }) => {
        if (row.index === dueBody.length - 1) {
          cell.styles.fillColor = [255, 243, 224];
          cell.styles.fontStyle = "bold";
          cell.styles.textColor = C.orange;
        }
      },
      margin: { left: 20, right: 20 },
    });
  }

  // ── footers + save ─────────────────────────────────────────────────────────
  drawFooter();
  doc.save(filename);
};

// ─── COMPONENT ────────────────────────────────────────────────────────────────
const ExportpdfSell = ({ data = [], filters = {} }) => {
  const [loading, setLoading] = useState(false);

  const handleExport = async () => {
    if (!data.length) return;
    setLoading(true);
    try {
      await generatePDF(data, filters);
    } catch (e) {
      console.error("PDF export error:", e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Tooltip title={`Export ${data.length} EV records to PDF`} arrow>
      <span>
        <Button
          variant="outlined"
          size="small"
          startIcon={loading ? <CircularProgress size={14} /> : <PictureAsPdfIcon />}
          onClick={handleExport}
          disabled={loading || !data.length}
          sx={{
            borderRadius: 2,
            fontWeight: 700,
            fontSize: "0.75rem",
            color: "#c62828",
            borderColor: "#c62828",
            "&:hover": { bgcolor: "#fce4ec", borderColor: "#b71c1c" },
          }}
        >
          {loading ? "Generating…" : "Export PDF"}
        </Button>
      </span>
    </Tooltip>
  );
};

export default ExportpdfSell;